import React, { ReactElement, useCallback, useContext } from "react"
import { Box, CircularProgress, Typography } from "@mui/material"
import { Checkbox } from "../../../../mp-shared-components/generic-shared-components/components"
import TableCell from "./TableCell"
import RowToggleButton from "./RowToggleButton"
import TableStyleContext from "../TableStyleContext"
import VirtualTableContext from "../VirtualTableContext"
import { Column, TableRecordType } from "../types"

type Props = {
  idProp?: string
  index: number
  record: TableRecordType
  selectedRows: string[]
  selectDisabled?: boolean
  displayedProperties: string[]
  headerColumns: Column[]
  selectable?: boolean
  expandable?: boolean
  onRowExpand?: (id: string) => void
  isRowExpanded?: (id: string) => boolean
  isRowLoading?: (id: string) => boolean
  hasDiagonalHeaders?: boolean
  stickyFirstColumn?: boolean
  hasHorizontalScroll?: boolean
  onSelectRow: (id: string) => void
  onClick?: (id: string, record: any, isTargetBlank?: boolean) => void
  getRowAction?: (
    id: string,
    record?: { [key: string]: string }
  ) => ReactElement
  children?: (prop: string, record: TableRecordType) => React.ReactNode
  renderSelectionCell?: (params: {
    record: TableRecordType
    isSelected: boolean
    disabled: boolean
    onToggle: () => void
  }) => React.ReactNode
}

const TableRowComponent: React.FC<Props> = ({
  idProp = "id",
  index,
  record,
  selectedRows,
  selectDisabled = false,
  displayedProperties,
  headerColumns,
  hasHorizontalScroll,
  selectable = false,
  expandable = false,
  onRowExpand,
  isRowExpanded,
  isRowLoading,
  onSelectRow,
  onClick,
  getRowAction,
  hasDiagonalHeaders = false,
  stickyFirstColumn = false,
  children,
  renderSelectionCell
}) => {
  const { cx, classes } = useContext(TableStyleContext)
  const { table, rowVirtualizer } = useContext(VirtualTableContext)

  const virtualRow = rowVirtualizer
    .getVirtualItems()
    .find(virtualRow => virtualRow.index === index)
  const row = table.getRowModel().rows[index]

  // Determine if this row can be expanded (via custom handler or built-in)
  const hasCustomExpandHandler = expandable && onRowExpand

  // Get nesting level for indentation (from record or parentId)
  const nestingLevel = record._depth ?? (record.parentId ? 1 : 0)

  const handleExpandClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (hasCustomExpandHandler && onRowExpand) {
        onRowExpand(record[idProp])
      } else if (row) {
        row.getToggleExpandedHandler()()
      }
    },
    [hasCustomExpandHandler, onRowExpand, record, idProp, row]
  )

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const el = event.target as HTMLElement

      if (["A", "BUTTON"].includes(el.tagName)) return

      onClick?.(record[idProp], record, event.metaKey || event.ctrlKey)
    },
    [onClick, record, idProp]
  )

  const handleRowSelect = useCallback(() => {
    onSelectRow?.(record[idProp])
  }, [onSelectRow, record, idProp])

  // Safety check: if row or virtualRow doesn't exist yet (during data transition), don't render
  if (!row || !virtualRow) {
    return null
  }

  const cells = row.getVisibleCells()
  const hasSubheaders = cells.some(cell => !!cell.column.parent)

  // Check if using container scroll (no scrollMargin) or window scroll
  const scrollMargin =
    "scrollMargin" in rowVirtualizer.options
      ? rowVirtualizer.options.scrollMargin || 0
      : 0

  const isContainerScroll = scrollMargin === 0

  // For custom expand handler, use _canExpand from record; otherwise use tanstack's built-in
  const canExpand = hasCustomExpandHandler
    ? record._canExpand === true
    : row.getCanExpand()
  const isExpanded = hasCustomExpandHandler
    ? isRowExpanded?.(record[idProp])
    : row.getIsExpanded()
  const isLoading = hasCustomExpandHandler && isRowLoading?.(record[idProp])

  return (
    <Box
      role="row"
      // Needed for dynamic row height measurement
      data-index={index}
      ref={node => rowVirtualizer.measureElement(node)}
      id={record[idProp]}
      className={cx(classes.row, {
        [classes.selectedRow]: selectedRows?.includes(record[idProp]),
        [classes.rowEven]: index % 2 === 0,
        [classes.clickableRow]: !!onClick,
        [classes.inheritWidth]: hasHorizontalScroll
      })}
      style={{
        position: "absolute",
        transform: `translateY(${
          isContainerScroll ? virtualRow.start : virtualRow.start - scrollMargin
        }px)`
      }}
      data-automation-id="data-table-row"
    >
      {selectable && (
        <TableCell
          role="cell"
          className={cx(classes.bodyTableCell, classes.checkboxCell)}
          data-automation-id="data-table-checkbox-cell"
        >
          {/* Only show checkbox for top-level rows, not for nested children */}
          {nestingLevel === 0 &&
            (renderSelectionCell ? (
              renderSelectionCell({
                record,
                isSelected: selectedRows?.includes(record[idProp]) || false,
                disabled: selectDisabled,
                onToggle: handleRowSelect
              })
            ) : (
              <Checkbox
                name="data-table-row"
                checked={selectedRows?.includes(record[idProp])}
                onChange={handleRowSelect}
                disabled={selectDisabled}
                className={classes.checkbox}
                data-automation-id="data-table-checkbox"
                // a11y: name the row-select checkbox (SC 4.1.2)
                inputProps={{ "aria-label": "Select row" }}
              />
            ))}
        </TableCell>
      )}
      {row.getVisibleCells().map((cell, i, arr) => {
        const isSameGroup =
          cell.column.parent &&
          cell.column.parent.id === arr[i + 1]?.column?.parent?.id

        const columnAlign = (cell.column.columnDef.meta as any)?.align || "left"
        const isMasked = (cell.column.columnDef.meta as any)?.mask === true
        const columnDef = cell.column.columnDef as any
        const isDiagonalHeader = columnDef.diagonalHeader || false

        const isFirstDiagonalHeader =
          isDiagonalHeader &&
          !arr.slice(0, i).some(c => (c.column.columnDef as any).diagonalHeader)

        return (
          <TableCell
            role="cell"
            key={cell.id}
            className={cx(classes.bodyTableCell, {
              [classes.bodyTableCellWithDiagonalHeaders]: hasDiagonalHeaders,
              [classes.bodyTableCellDiagonal]:
                hasDiagonalHeaders && !isDiagonalHeader,
              [classes.firstDiagonalColumn]:
                hasDiagonalHeaders && isFirstDiagonalHeader,
              [classes.firstColumn]: stickyFirstColumn && i === 0,
              [classes.expandableCell]: canExpand && i === 0,
              [classes.expandedCell]: nestingLevel > 0 && i === 0,
              [classes.groupedCell]: hasSubheaders,
              [classes.groupCell]: isSameGroup,
              [classes[`align_${columnAlign}`]]: true,
              "clarity-mask": isMasked
            })}
            onClick={handleRowClick}
            data-automation-id="data-table-cell"
            style={{
              width: cell.column.getSize(),
              flex: isDiagonalHeader
                ? "none"
                : cell.column.getCanResize()
                ? undefined
                : "none",
              ...(nestingLevel > 0 &&
                i === 0 && { paddingLeft: `${42 * nestingLevel}px` })
            }}
          >
            {/* If row can be expanded, render toggle button or loading spinner in 1st cell. */}
            {canExpand && i === 0 && (
              <Box display="inline-flex" alignItems="center" mr={1}>
                {isLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <RowToggleButton
                    expanded={isExpanded}
                    onClick={handleExpandClick}
                  />
                )}
              </Box>
            )}

            {children?.(cell.column.columnDef.id, record) || (
              <Typography variant="body2">
                {record[cell.column.columnDef.id] || "- -"}
              </Typography>
            )}
          </TableCell>
        )
      })}
      {(!!getRowAction || !!displayedProperties) && (
        <>
          {/* Spacer cell before action cell - only for diagonal headers. */}
          {hasDiagonalHeaders && (
            <TableCell
              role="cell"
              className={cx(classes.tableCell, classes.spacerCell)}
            />
          )}
          <TableCell
            role="cell"
            className={cx(classes.bodyTableCell, classes.actionCell)}
            data-automation-id="data-table-action-cell"
          >
            {/* Only show row actions for top-level rows and level-1 child rows for SuperAdmin */}
            {(nestingLevel === 0 || nestingLevel === 1) &&
              getRowAction?.(record[idProp], record)}
          </TableCell>
        </>
      )}
    </Box>
  )
}

export default TableRowComponent
